import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Upload, Package, CheckCircle2, Clock, Truck, PackageCheck, XCircle, Home, ArrowRight, Pencil, Settings, FileSpreadsheet, ListChecks } from "lucide-react";
import { OutstandingItems } from "@/components/kit/OutstandingItems";
import { format, parseISO } from "date-fns";

const ENT_OPTIONS = [["base_kit", "Base kit (player kit)"], ["playmaker_primary", "Playmaker · Primary"], ["playmaker_secondary", "Playmaker · Secondary"], ["qualified_primary", "Fully qualified · Primary"], ["qualified_secondary", "Fully qualified · Secondary"]];
const ENT_LABEL = Object.fromEntries(ENT_OPTIONS);
const itemEnts = (c) => (c?.entitlements && c.entitlements.length ? c.entitlements : c?.entitlement ? [c.entitlement] : []);

const STAGES = [
  { key: "submitted", label: "Submitted", icon: Clock },
  { key: "approved", label: "Approved", icon: CheckCircle2 },
  { key: "ordered", label: "Ordered", icon: Package },
  { key: "arrived", label: "Arrived at club", icon: Home },
  { key: "delivered", label: "Delivered", icon: PackageCheck },
];

const STATUS_TONE = {
  submitted: "bg-muted text-foreground border-border",
  approved: "bg-blue-500/20 text-blue-400 border-blue-500/40",
  ordered: "bg-[#83B68B]/20 text-[#83B68B] border-[#83B68B]/40",
  arrived: "bg-purple-500/20 text-purple-400 border-purple-500/40",
  delivered: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
  cancelled: "bg-red-500/20 text-red-400 border-red-500/40",
};

const STATUS_LABEL = {
  submitted: "Submitted", approved: "Approved", ordered: "Ordered",
  arrived: "Arrived at club", delivered: "Delivered", cancelled: "Cancelled",
};

const emptyLine = { item_name: "", color: "", size: "", quantity: 1, squad_ref: "", assigned_to: "", coach_id: "", notes: "" };
const newLine = () => ({ ...emptyLine, _key: Math.random().toString(36).slice(2) });

function orders_selected_summary(orders, ids) {
  const sel = orders.filter(o => ids.includes(o.id));
  const total = sel.reduce((s, o) => s + (o.items?.reduce((a, l) => a + (parseInt(l.quantity) || 0), 0) || 0), 0);
  const refs = sel.map(o => o.order_ref).slice(0, 6).join(", ");
  const suffix = sel.length > 6 ? ` …+${sel.length - 6} more` : "";
  return `${refs}${suffix}\nTotal quantity: ${total}`;
}

function StatusTimeline({ status }) {
  const currentIdx = STAGES.findIndex(s => s.key === status);
  const cancelled = status === "cancelled";
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="order-timeline">
      {STAGES.map((s, i) => {
        const done = !cancelled && i <= currentIdx;
        const active = !cancelled && i === currentIdx;
        const Icon = s.icon;
        return (
          <div key={s.key} className="flex items-center gap-1">
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-[10px] tracking-widest uppercase ${cancelled ? "bg-muted opacity-30 border-border" : active ? "bg-[#83B68B]/20 text-[#83B68B] border-[#83B68B]/50" : done ? "bg-muted text-foreground border-border" : "bg-muted opacity-40 border-border"}`}>
              <Icon className="w-3 h-3" /> {s.label}
            </div>
            {i < STAGES.length - 1 && <ArrowRight className={`w-3 h-3 ${cancelled ? "opacity-20" : done ? "text-[#83B68B]" : "opacity-30"}`} />}
          </div>
        );
      })}
      {cancelled && <span className="text-[10px] tracking-widest uppercase px-2 py-1 rounded bg-red-500/20 text-red-400 border border-red-500/40 ml-2"><XCircle className="w-3 h-3 inline mr-1" /> Cancelled</span>}
    </div>
  );
}

export default function KitOrders() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_kit_admin === true;
  const [orders, setOrders] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [openOutstanding, setOpenOutstanding] = useState(false);
  const [openCatalogue, setOpenCatalogue] = useState(false);
  const [openDetail, setOpenDetail] = useState(null); // order obj
  const [filterStatus, setFilterStatus] = useState("active");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ order_type: "kit", team_id: "", squad: "", notes: "", items: [newLine()], ordered_by_id: "", ordered_at: "" });
  const isKit = form.order_type === "kit";
  const TYPE_LABEL = { kit: "Kit", equipment: "Equipment", first_aid: "First Aid Supplies" };
  const typeLabel = TYPE_LABEL[form.order_type] || "Kit";
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [shopForm, setShopForm] = useState({ shop_name: "", shop_contact: "", shop_order_number: "" });
  const [shopBusy, setShopBusy] = useState(false);
  const COACH_LOCKED = new Set(["ordered", "arrived", "delivered", "cancelled"]);
  const csvInputRef = useRef(null);
  const [csvTeam, setCsvTeam] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [orderAuthors, setOrderAuthors] = useState([]);

  const load = async () => {
    try {
      const [ordersRes, catRes, teamsRes] = await Promise.all([
        api.get("/kit/orders"), api.get("/kit/catalogue"), api.get("/teams"),
      ]);
      setOrders(ordersRes.data);
      setCatalogue(catRes.data);
      setTeams(teamsRes.data);
      if (isAdmin) {
        try { const a = await api.get("/kit/order-authors"); setOrderAuthors(a.data); }
        catch (err) { console.warn("Could not load order authors:", err); }
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const [entitlement, setEntitlement] = useState(null);
  useEffect(() => { api.get("/kit/my-entitlement").then(r => setEntitlement(r.data)).catch(() => {}); }, [openNew]);
  useEffect(() => {
    if (openDetail) setShopForm({ shop_name: openDetail.shop_name || "", shop_contact: openDetail.shop_contact || "", shop_order_number: openDetail.shop_order_number || "" });
  }, [openDetail?.id]);

  const setLineDelivery = async (o, idx, delivered, note) => {
    try {
      const { data } = await api.patch(`/kit/orders/${o.id}/lines/${idx}/delivery`, { delivered, note: note ?? o.items[idx]?.delivery_note ?? null });
      setOrders(prev => prev.map(x => x.id === o.id ? data : x));
      setOpenDetail(data);
      if (data.status === "delivered" && o.status !== "delivered") toast.success("Every item delivered · order marked Delivered and coach notified");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [kitMode, setKitMode] = useState("player"); // player = base kit only · coach = tier items
  const activeCatalogue = useMemo(() => {
    let list = catalogue.filter(c => c.active !== false && (c.category || "kit") === form.order_type);
    if (form.order_type !== "kit") return list;
    if (isAdmin) return list;
    if (kitMode === "player") return list.filter(c => itemEnts(c).every(e => e === "base_kit"));
    const tier = entitlement?.tier;
    return list.filter(c => itemEnts(c).some(e => e.startsWith(tier || "__")));
  }, [catalogue, form.order_type, kitMode, entitlement, isAdmin]);
  const slotFor = (c) => { const e = itemEnts(c).find(x => x !== "base_kit" && x.startsWith(entitlement?.tier || "")); return e ? (e.endsWith("primary") ? "Primary" : "Secondary") : null; };
  const filtered = useMemo(() => filterStatus === "all" ? orders : filterStatus === "active" ? orders.filter(o => o.status !== "delivered" && o.status !== "cancelled") : orders.filter(o => o.status === filterStatus), [orders, filterStatus]);

  const updateLine = (idx, patch) => setForm(f => ({ ...f, items: f.items.map((l, i) => i === idx ? { ...l, ...patch } : l) }));
  const addLine = () => setForm(f => ({ ...f, items: [...f.items, newLine()] }));
  const removeLine = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const submit = async () => {
    if (!form.items.length || !form.items.some(l => l.item_name)) return toast.error("Add at least one item");
    setBusy(true);
    try {
      const cleaned = form.items.filter(l => l.item_name).map(l => ({
        item_name: l.item_name, color: l.color || null, size: l.size || null,
        quantity: Math.max(1, parseInt(l.quantity) || 1),
        squad_ref: isKit ? (l.squad_ref || null) : null,
        assigned_to: isKit ? (l.assigned_to || null) : null,
        coach_id: isAdmin ? (l.coach_id || null) : null,
        notes: l.notes || null,
      }));
      if (editingOrderId) {
        // PUT — amends items / team / squad / notes on an existing order. Backend
        // blocks coaches once the order is with the shop (submitted / approved only).
        const { data } = await api.put(`/kit/orders/${editingOrderId}`, {
          team_id: form.team_id || null, squad: form.squad || null,
          notes: form.notes || null, items: cleaned,
        });
        toast.success("Order updated");
        if (openDetail?.id === editingOrderId) setOpenDetail(data);
      } else {
        await api.post("/kit/orders", {
          order_type: form.order_type,
          team_id: form.team_id || null, squad: form.squad || null,
          notes: form.notes || null, items: cleaned,
          ordered_by_id: isAdmin ? (form.ordered_by_id || null) : null,
          ordered_at: isAdmin && form.ordered_at ? new Date(form.ordered_at).toISOString() : null,
        });
        toast.success("Order submitted for admin review");
      }
      setOpenNew(false);
      setEditingOrderId(null);
      setForm({ order_type: "kit", team_id: "", squad: "", notes: "", items: [newLine()], ordered_by_id: "", ordered_at: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openEdit = (o) => {
    setEditingOrderId(o.id);
    setForm({
      order_type: o.order_type || "kit",
      team_id: o.team_id || "",
      squad: o.squad || "",
      notes: o.notes || "",
      items: (o.items || []).map(l => ({
        ...l,
        coach_id: l.coach_id || "",
        _key: Math.random().toString(36).slice(2),
      })) || [newLine()],
      ordered_by_id: "",
      ordered_at: "",
    });
    setOpenNew(true);
  };

  const cancelOrder = async (o) => {
    const note = window.prompt(`Cancel order ${o.order_ref}? Optional note for the coach:`);
    if (note === null) return;
    try {
      const { data } = await api.patch(`/kit/orders/${o.id}/status`, { status: "cancelled", note: note || null });
      toast.success("Order cancelled · coach notified by email");
      setOrders(prev => prev.map(x => x.id === o.id ? data : x));
      if (openDetail?.id === o.id) setOpenDetail(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const downloadOrder = async (o) => {
    try {
      const resp = await api.get(`/kit/orders/${o.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${o.order_ref}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      let detail = err.response?.data;
      if (detail instanceof Blob) { try { detail = JSON.parse(await detail.text()); } catch {} }
      toast.error(formatApiError(detail?.detail || err.message));
    }
  };

  const saveShopDetails = async () => {
    if (!openDetail) return;
    setShopBusy(true);
    try {
      const { data } = await api.patch(`/kit/orders/${openDetail.id}/shop-details`, shopForm);
      toast.success("Shop details saved");
      setOpenDetail(data);
      setOrders(prev => prev.map(x => x.id === data.id ? data : x));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setShopBusy(false); }
  };

  const onCsvFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const qs = `?order_type=${form.order_type}${csvTeam ? `&team_id=${csvTeam}` : ""}`;
      const { data } = await api.post(`/kit/orders/upload${qs}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${data.rows} row(s) as ${data.created.order_ref}`);
      setOpenNew(false); setCsvTeam(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const moveStatus = async (o, next) => {
    const note = window.prompt(`Move ${o.order_ref} → ${STATUS_LABEL[next]}?\n\nOptional note for the coach (leave blank if none). Press OK to confirm, or Cancel to leave the order unchanged.`);
    if (note === null) return;
    try {
      const { data } = await api.patch(`/kit/orders/${o.id}/status`, { status: next, note: note || null });
      toast.success("Status updated · coach notified by email");
      setOrders(prev => prev.map(x => x.id === o.id ? data : x));
      if (openDetail?.id === o.id) setOpenDetail(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeOrder = async (o) => {
    if (!window.confirm(`Delete order ${o.order_ref}?`)) return;
    try { await api.delete(`/kit/orders/${o.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectableIds = useMemo(() => filtered.filter(o => o.status !== "cancelled").map(o => o.id), [filtered]);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));
  const toggleSelectAll = () => setSelected(prev => {
    if (allSelected) {
      const next = new Set(prev);
      selectableIds.forEach(id => next.delete(id));
      return next;
    }
    const next = new Set(prev);
    selectableIds.forEach(id => next.add(id));
    return next;
  });

  const runExport = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const summary = orders_selected_summary(orders, ids);
    if (!window.confirm(`Generate shop order for ${ids.length} order(s)?\n\n${summary}\n\nThey will move to "Ordered" status and coaches will be emailed.`)) return;
    setExporting(true);
    try {
      const res = await api.post("/admin/kit/orders/export", { order_ids: ids, advance_to_ordered: true }, { responseType: "blob" });
      const ref = res.headers?.["x-export-ref"] || "PK";
      // Prefer server-supplied filename if present
      const cd = res.headers?.["content-disposition"] || "";
      let filename = `EYFC Prokit - ${ref}.xlsx`;
      const m = /filename\*=UTF-8''([^;]+)/i.exec(cd);
      if (m) { try { filename = decodeURIComponent(m[1]); } catch {} }
      else {
        const m2 = /filename="?([^";]+)"?/i.exec(cd);
        if (m2) filename = m2[1];
      }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Shop order ${ref} generated`);
      setSelected(new Set());
      load();
    } catch (err) {
      let detail = err.response?.data;
      if (detail instanceof Blob) { try { detail = JSON.parse(await detail.text()); } catch {} }
      toast.error(formatApiError(detail?.detail || err.message));
    } finally { setExporting(false); }
  };

  const nextStatusChoices = (current) => {
    if (current === "cancelled" || current === "delivered") return [];
    const idx = STAGES.findIndex(s => s.key === current);
    const forward = idx >= 0 && idx < STAGES.length - 1 ? [STAGES[idx + 1].key] : [];
    return [...forward, "cancelled"];
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Kit &amp; equipment</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2">Kit & Equipment Orders</h1>
          <p className="opacity-60 mt-2 text-sm">{isAdmin ? "Review, approve and track every kit order the club receives." : "Submit a new order and follow its progress through each stage."}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <button onClick={()=>setOpenCatalogue(true)} className="px-4 py-2.5 rounded-md border border-border hover:border-[#83B68B] hover:text-[#83B68B] text-sm inline-flex items-center gap-2" data-testid="kit-manage-catalogue">
              <Settings className="w-4 h-4" /> Manage catalogue
            </button>
          )}
          {isAdmin && selected.size > 0 && (
            <button onClick={runExport} disabled={exporting} className="btn-volt px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="kit-generate-order-btn">
              <FileSpreadsheet className="w-4 h-4" />
              {exporting ? "Generating…" : `Generate Shop Order (${selected.size})`}
            </button>
          )}
          <button onClick={()=>setOpenOutstanding(true)} className="px-4 py-2.5 rounded-md border border-border hover:border-[#83B68B] hover:text-[#83B68B] text-sm inline-flex items-center gap-2" data-testid="kit-outstanding-btn">
            <ListChecks className="w-4 h-4" /> Outstanding items
          </button>
          <OutstandingItems open={openOutstanding} onOpenChange={setOpenOutstanding} isAdmin={isAdmin} onExported={load} />
          <button onClick={()=>setOpenNew(true)} className="btn-volt px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2" data-testid="kit-new-order-btn">
            <Plus className="w-4 h-4" /> New order
          </button>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-3 items-center card-tactical rounded-md p-4">
        <div className="text-[10px] tracking-widest uppercase opacity-60 mr-1">Filter</div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48" data-testid="kit-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active (not delivered)</SelectItem>
            <SelectItem value="all">All statuses</SelectItem>
            {STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        {isAdmin && selectableIds.length > 0 && (
          <button onClick={toggleSelectAll} className="text-xs tracking-widest uppercase px-3 py-1.5 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B]" data-testid="kit-select-all-btn">
            {allSelected ? "Clear selection" : `Select all (${selectableIds.length})`}
          </button>
        )}
        <div className="flex-1" />
        {isAdmin && selected.size > 0 && (
          <div className="text-xs text-[#83B68B] font-medium" data-testid="kit-selection-count">{selected.size} selected for shop export</div>
        )}
        <div className="text-xs opacity-60" data-testid="kit-filter-count">{filtered.length} of {orders.length}</div>
      </div>

      {loading ? (
        <div className="opacity-50 font-display tracking-widest text-sm">LOADING…</div>
      ) : filtered.length === 0 ? (
        <div className="card-tactical rounded-md p-10 text-center opacity-60">{orders.length === 0 ? "No kit orders yet." : "No orders match that filter."}</div>
      ) : (
        <div className="space-y-3" data-testid="kit-orders-list">
          {filtered.map(o => (
            <div key={o.id} className={`card-tactical rounded-md p-5 ${selected.has(o.id) ? "ring-2 ring-[#83B68B]/60" : ""}`} data-testid={`kit-order-${o.id}`}>
              <div className="flex flex-wrap gap-3 items-start justify-between">
                <div className="flex items-start gap-3">
                  {isAdmin && o.status !== "cancelled" && (
                    <input
                      type="checkbox"
                      checked={selected.has(o.id)}
                      onChange={()=>toggleSelect(o.id)}
                      className="mt-1.5 w-4 h-4 accent-[#83B68B] cursor-pointer"
                      title="Include in shop export"
                      data-testid={`kit-order-select-${o.id}`}
                    />
                  )}
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold text-lg tracking-tight">{o.order_ref}</span>
                      {o.order_type && o.order_type !== "kit" && <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border border-[#83B68B]/40 text-[#83B68B]" data-testid={`kit-order-type-${o.id}`}>{o.order_type === "first_aid" ? "First Aid" : "Equipment"}</span>}
                      <span className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded border ${STATUS_TONE[o.status]}`} data-testid={`kit-order-status-${o.id}`}>{STATUS_LABEL[o.status]}</span>
                      {o.viewer_scope === "line" && (
                        <span className="text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-[#83B68B]/40 text-[#83B68B] bg-[#83B68B]/10" title="This order was placed by an admin — you can see only the items assigned to you.">
                          Your items only
                        </span>
                      )}
                      {o.shop_order_number && (
                        <span className="text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-border opacity-70" title="Shop order reference" data-testid={`kit-order-shopref-${o.id}`}>
                          Order no. {o.shop_order_number}
                        </span>
                      )}
                      {(() => { const d = (o.items || []).filter(l => l.delivered_at).length; const n = (o.items || []).length; return d > 0 && d < n && o.status !== "cancelled" ? <span className="text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-amber-400/40 text-amber-300 bg-amber-500/10" data-testid={`kit-order-partial-${o.id}`}>{d}/{n} delivered</span> : null; })()}
                    </div>
                    <div className="text-xs opacity-60 mt-1">
                      <span className="tracking-widest uppercase opacity-70">Ordered by</span> {o.coach_name}
                      {o.logged_by_name && <span className="opacity-70"> · logged by {o.logged_by_name}</span>}
                      {o.team_name && ` · ${o.team_name}`}
                      {o.age_group && ` · ${o.age_group}`}
                      {o.squad && ` · ${o.squad}`}
                      {" · "}{o.items?.length || 0} item{(o.items?.length || 0) === 1 ? "" : "s"}
                      {" · "}{o.created_at && format(parseISO(o.created_at), "d MMM yyyy · HH:mm")}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={()=>setOpenDetail(o)} className="px-3 py-1.5 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] text-xs tracking-widest uppercase" data-testid={`kit-order-view-${o.id}`}>View</button>
                  {isAdmin && nextStatusChoices(o.status).map(ns => (
                    <button key={ns} onClick={()=>moveStatus(o, ns)} className={`px-3 py-1.5 rounded border text-xs tracking-widest uppercase ${ns === "cancelled" ? "border-red-500/40 hover:border-red-500 text-red-400" : "border-[#83B68B]/40 hover:border-[#83B68B] text-[#83B68B]"}`} data-testid={`kit-order-advance-${o.id}-${ns}`}>
                      → {STATUS_LABEL[ns]}
                    </button>
                  ))}
                  {(isAdmin || (o.coach_id === user?.id && o.status === "submitted")) && (
                    <button onClick={()=>removeOrder(o)} className="p-1.5 opacity-50 hover:opacity-100 hover:text-red-500" title="Delete order" data-testid={`kit-order-delete-${o.id}`}><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
              <div className="mt-3"><StatusTimeline status={o.status} /></div>
            </div>
          ))}
        </div>
      )}

      {/* New order dialog */}
      <Dialog open={openNew} onOpenChange={(v)=>{
        setOpenNew(v);
        if (!v) {
          setEditingOrderId(null);
          setForm({ order_type: "kit", team_id: "", squad: "", notes: "", items: [newLine()], ordered_by_id: "", ordered_at: "" });
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">{editingOrderId ? `Amend ${typeLabel.toLowerCase()} order` : "New order"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {!editingOrderId && (
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">What are you ordering?</Label>
                <div className="grid grid-cols-3 gap-2 mt-2" data-testid="kit-form-order-type">
                  {Object.entries(TYPE_LABEL).map(([k, label]) => (
                    <button key={k} type="button" onClick={() => setForm(f => ({ ...f, order_type: k, items: [newLine()] }))} data-testid={`kit-form-type-${k}`}
                      className={`py-2.5 rounded-md border text-[10px] tracking-widest uppercase ${form.order_type === k ? "border-[#83B68B] bg-[#83B68B]/10 text-[#83B68B]" : "border-border opacity-70 hover:opacity-100"}`}>{label}</button>
                  ))}
                </div>
              </div>
            )}
            {!editingOrderId && isKit && !isAdmin && (
              <div data-testid="kit-form-kit-mode">
                <Label className="text-xs uppercase tracking-widest opacity-70">Who is this kit for?</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button type="button" onClick={() => { setKitMode("player"); setForm(f => ({ ...f, items: [newLine()] })); }} data-testid="kit-mode-player"
                    className={`py-2.5 rounded-md border text-[10px] tracking-widest uppercase ${kitMode === "player" ? "border-[#83B68B] bg-[#83B68B]/10 text-[#83B68B]" : "border-border opacity-70 hover:opacity-100"}`}>Player kit</button>
                  <button type="button" onClick={() => { setKitMode("coach"); setForm(f => ({ ...f, items: [newLine()] })); }} data-testid="kit-mode-coach" disabled={!entitlement?.tier}
                    className={`py-2.5 rounded-md border text-[10px] tracking-widest uppercase disabled:opacity-40 ${kitMode === "coach" ? "border-[#83B68B] bg-[#83B68B]/10 text-[#83B68B]" : "border-border opacity-70 hover:opacity-100"}`}>My coach kit</button>
                </div>
                {kitMode === "player" && <p className="text-[11px] opacity-60 mt-1.5">Base kit items only. Ordering a home top adds the matching away top in the same size and number automatically.</p>}
                {kitMode === "coach" && entitlement?.tier && (
                  <p className="text-[11px] opacity-70 mt-1.5" data-testid="kit-entitlement-note">
                    {entitlement.tier === "playmaker" ? "Playmaker" : "Fully qualified"} coach · one primary + one secondary item per season{entitlement.season ? ` (${entitlement.season})` : ""}.
                    {" "}Primary: {entitlement.used?.primary ? <span className="text-amber-400">claimed — {entitlement.used.primary.item_name}</span> : <span className="text-[#83B68B]">available</span>} ·
                    {" "}Secondary: {entitlement.used?.secondary ? <span className="text-amber-400">claimed — {entitlement.used.secondary.item_name}</span> : <span className="text-[#83B68B]">available</span>}
                  </p>
                )}
                {!entitlement?.tier && <p className="text-[11px] opacity-60 mt-1.5">Coach kit unlocks once your coaching qualification (Playmaker / Level 1+) is recorded on your coach profile.</p>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Team</Label>
                <Select value={form.team_id || "none"} onValueChange={v=>setForm({...form, team_id: v === "none" ? "" : v})}>
                  <SelectTrigger data-testid="kit-form-team" className="mt-2"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— no team —</SelectItem>
                    {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.age_group ? ` · ${t.age_group}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Squad (optional)</Label>
                <Input value={form.squad} onChange={e=>setForm({...form, squad: e.target.value})} placeholder="e.g. Aylett" data-testid="kit-form-squad" className="mt-2" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest opacity-70">Order notes</Label>
              <Input value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})} placeholder="Anything the admin should know" data-testid="kit-form-notes" className="mt-2" />
            </div>

            {isAdmin && (
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border">
                <div>
                  <Label className="text-xs uppercase tracking-widest opacity-70">Ordered by <span className="opacity-50">(kit admin override)</span></Label>
                  <Select value={form.ordered_by_id || "__me"} onValueChange={v=>setForm({...form, ordered_by_id: v === "__me" ? "" : v})}>
                    <SelectTrigger className="mt-2" data-testid="kit-form-ordered-by"><SelectValue placeholder="— me —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__me">— me ({user?.name || "you"}) —</SelectItem>
                      {orderAuthors.filter(a => a.id !== user?.id).map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.name}{a.role !== "coach" ? ` · ${a.role}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest opacity-70">Order date <span className="opacity-50">(defaults to now)</span></Label>
                  <Input type="date" value={form.ordered_at} onChange={e=>setForm({...form, ordered_at: e.target.value})} className="mt-2" data-testid="kit-form-ordered-at" />
                </div>
              </div>
            )}

            <div className="border border-border rounded-md p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] tracking-widest uppercase text-[#83B68B]">Line items</div>
                <button onClick={addLine} data-testid="kit-form-add-line" className="text-xs tracking-widest uppercase px-3 py-1.5 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add row</button>
              </div>
              <div className="space-y-3">
                {form.items.map((l, idx) => {
                  const cat = activeCatalogue.find(c => c.item_name === l.item_name);
                  return (
                    <div key={l._key || idx} className="border border-border/60 rounded p-2 space-y-2" data-testid={`kit-form-line-${idx}`}>
                      <div className="grid grid-cols-12 gap-2 items-start">
                        <div className="col-span-3">
                          <Select value={l.item_name || "__"} onValueChange={v=>updateLine(idx, { item_name: v === "__" ? "" : v, color: "", size: "" })}>
                            <SelectTrigger data-testid={`kit-line-item-${idx}`}><SelectValue placeholder="Item" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__">— choose —</SelectItem>
                              {activeCatalogue.map(c => <SelectItem key={c.id} value={c.item_name}>{c.item_name}{c.product_code ? ` · ${c.product_code}` : ""}{slotFor(c) ? ` · ${slotFor(c)}` : ""}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          {cat && cat.colors?.length > 0 ? (
                            <Select value={l.color || "__"} onValueChange={v=>updateLine(idx, { color: v === "__" ? "" : v })}>
                              <SelectTrigger data-testid={`kit-line-color-${idx}`}><SelectValue placeholder="Colour" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__">— none —</SelectItem>
                                {cat.colors.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input value={l.color} onChange={e=>updateLine(idx, { color: e.target.value })} placeholder="Colour" data-testid={`kit-line-color-${idx}`} />
                          )}
                        </div>
                        <div className="col-span-2">
                          {cat && cat.sizes?.length > 0 ? (
                            <Select value={l.size || "__"} onValueChange={v=>updateLine(idx, { size: v === "__" ? "" : v })}>
                              <SelectTrigger data-testid={`kit-line-size-${idx}`}><SelectValue placeholder="Size" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__">— none —</SelectItem>
                                {cat.sizes.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input value={l.size} onChange={e=>updateLine(idx, { size: e.target.value })} placeholder="Size" data-testid={`kit-line-size-${idx}`} />
                          )}
                        </div>
                        <div className="col-span-1">
                          <Input type="number" min="1" value={l.quantity} onChange={e=>updateLine(idx, { quantity: e.target.value })} data-testid={`kit-line-qty-${idx}`} />
                        </div>
                        {isKit ? (<>
                        <div className="col-span-1">
                          <Input value={l.squad_ref} onChange={e=>updateLine(idx, { squad_ref: e.target.value })} placeholder="#/init" title="Squad number or initials — shows in the shop order in brackets" data-testid={`kit-line-squad-${idx}`} />
                        </div>
                        <div className="col-span-2">
                          <Input value={l.assigned_to} onChange={e=>updateLine(idx, { assigned_to: e.target.value })} placeholder="Player name (optional)" data-testid={`kit-line-assigned-${idx}`} />
                        </div>
                        </>) : (
                        <div className="col-span-3">
                          <Input value={l.notes || ""} onChange={e=>updateLine(idx, { notes: e.target.value })} placeholder="Line note (optional)" data-testid={`kit-line-note-${idx}`} />
                        </div>
                        )}
                        <div className="col-span-1 flex items-center justify-end">
                          {form.items.length > 1 && (
                            <button onClick={()=>removeLine(idx)} className="p-2 opacity-60 hover:opacity-100 hover:text-red-500" title="Remove"><Trash2 className="w-4 h-4" /></button>
                          )}
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <Label className="col-span-3 text-[10px] tracking-widest uppercase opacity-60 text-right pr-2">Assign to coach</Label>
                          <div className="col-span-8">
                            <Select value={l.coach_id || "__"} onValueChange={v=>updateLine(idx, { coach_id: v === "__" ? "" : v })}>
                              <SelectTrigger data-testid={`kit-line-coach-${idx}`} className="h-9"><SelectValue placeholder="— order owner —" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__">— order owner (default) —</SelectItem>
                                {orderAuthors.map(a => (
                                  <SelectItem key={a.id} value={a.id}>{a.name}{a.role !== "coach" ? ` · ${a.role}` : ""}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {activeCatalogue.length === 0 && (
                <p className="text-xs opacity-60 mt-3">No catalogue items yet — {isAdmin ? "click 'Manage catalogue' to add some." : "ask an admin to set up the kit catalogue."}</p>
              )}
            </div>

            <div className="border border-border rounded-md p-3">
              <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-2">Or bulk-upload via CSV</div>
              <div className="flex flex-wrap gap-2 items-center">
                <Select value={csvTeam || "none"} onValueChange={v=>setCsvTeam(v === "none" ? "" : v)}>
                  <SelectTrigger className="w-52" data-testid="kit-csv-team"><SelectValue placeholder="Team (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— no team —</SelectItem>
                    {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.age_group ? ` · ${t.age_group}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button onClick={()=>csvInputRef.current?.click()} disabled={busy} className="px-4 py-2 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] text-xs tracking-widest uppercase inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="kit-csv-upload-btn">
                  <Upload className="w-3.5 h-3.5" /> Upload CSV
                </button>
                <input ref={csvInputRef} type="file" accept=".csv,text/csv" onChange={onCsvFile} className="hidden" />
                <button type="button" onClick={async () => { try { const r = await api.get(`/kit/orders/template?order_type=${form.order_type}`, { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `${form.order_type}-order-template.csv`; a.click(); URL.revokeObjectURL(a.href); } catch { toast.error("Couldn't download template"); } }} data-testid="kit-csv-template" className="text-xs opacity-60 hover:text-[#83B68B] underline">Download {typeLabel} template</button>
              </div>
              <p className="text-[10px] opacity-50 mt-2">The template lists every {typeLabel.toLowerCase()} product with its code and allowed colours/sizes — just fill in quantities (rows left at 0 are ignored). Item names/codes not on the product list are rejected.</p>
              {activeCatalogue.length > 0 && (
                <details className="mt-2 text-xs" data-testid="kit-product-list">
                  <summary className="cursor-pointer opacity-70 hover:opacity-100">View {typeLabel.toLowerCase()} product list ({activeCatalogue.length})</summary>
                  <ul className="mt-2 grid sm:grid-cols-2 gap-1 opacity-80">
                    {activeCatalogue.map(c => <li key={c.id}><span className="font-mono text-[#83B68B]">{c.product_code || "—"}</span> {c.item_name}{c.sizes?.length ? <span className="opacity-50"> · {c.sizes.join("/")}</span> : null}</li>)}
                  </ul>
                </details>
              )}
            </div>

            <button onClick={submit} disabled={busy} data-testid="kit-form-submit" className="btn-volt w-full py-3 rounded-md disabled:opacity-50">
              {busy ? "Saving…" : editingOrderId ? "Save amendments" : "Submit for review"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!openDetail} onOpenChange={(v)=>{ if(!v) setOpenDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {openDetail && (
            <>
              <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Order {openDetail.order_ref}</DialogTitle></DialogHeader>
              <div className="text-xs opacity-60">
                <span className="tracking-widest uppercase opacity-70">Ordered by</span> {openDetail.coach_name}
                {openDetail.logged_by_name && <span className="opacity-70"> · logged by {openDetail.logged_by_name}</span>}
                {" · "}{openDetail.team_name || "No team"}{openDetail.age_group ? ` · ${openDetail.age_group}` : ""}{openDetail.squad ? ` · ${openDetail.squad}` : ""} · Ordered on {openDetail.created_at && format(parseISO(openDetail.created_at), "d MMM yyyy HH:mm")}
              </div>
              {openDetail.notes && <div className="mt-3 text-sm opacity-90">"{openDetail.notes}"</div>}
              <div className="mt-4"><StatusTimeline status={openDetail.status} /></div>

              {/* Action row — Download for everyone, Amend for eligible coach / any admin,
                  Shop details + Cancel for admins only. */}
              <div className="mt-5 flex flex-wrap gap-2" data-testid="kit-detail-actions">
                <button
                  onClick={() => downloadOrder(openDetail)}
                  data-testid="kit-detail-download-btn"
                  className="text-xs tracking-widest uppercase px-4 py-2 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1.5"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5" /> Download
                </button>
                {(isAdmin || (openDetail.coach_id === user?.id && !COACH_LOCKED.has(openDetail.status))) && (
                  <button
                    onClick={() => openEdit(openDetail)}
                    data-testid="kit-detail-amend-btn"
                    className="text-xs tracking-widest uppercase px-4 py-2 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1.5"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Amend order
                  </button>
                )}
                {isAdmin && openDetail.status !== "cancelled" && (
                  <button
                    onClick={() => cancelOrder(openDetail)}
                    data-testid="kit-detail-cancel-btn"
                    className="text-xs tracking-widest uppercase px-4 py-2 rounded border border-red-500/40 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1.5"
                  >
                    <XCircle className="w-3.5 h-3.5" /> Cancel order
                  </button>
                )}
                {!isAdmin && openDetail.coach_id === user?.id && COACH_LOCKED.has(openDetail.status) && openDetail.status !== "cancelled" && (
                  <span className="text-[11px] opacity-60 self-center" data-testid="kit-detail-locked-note">
                    Order is with the shop — ask an admin to amend or cancel.
                  </span>
                )}
              </div>

              {/* Shop details — visible to everyone (read-only for coaches, form for admins). */}
              <div className="mt-6 border border-border rounded-md p-4" data-testid="kit-detail-shop">
                <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-3 inline-flex items-center gap-1.5">
                  <Settings className="w-3 h-3" /> Shop details
                </div>
                {isAdmin ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-[10px] tracking-widest uppercase opacity-70">Shop</Label>
                        <Input value={shopForm.shop_name} onChange={e=>setShopForm(s=>({...s, shop_name: e.target.value}))} placeholder="e.g. ProKit" data-testid="kit-shop-name" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-[10px] tracking-widest uppercase opacity-70">Contact</Label>
                        <Input value={shopForm.shop_contact} onChange={e=>setShopForm(s=>({...s, shop_contact: e.target.value}))} placeholder="Name / phone / email" data-testid="kit-shop-contact" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-[10px] tracking-widest uppercase opacity-70">Order no.</Label>
                        <Input value={shopForm.shop_order_number} onChange={e=>setShopForm(s=>({...s, shop_order_number: e.target.value}))} placeholder="Shop's order number for this order" data-testid="kit-shop-ref" className="mt-1" />
                      </div>
                    </div>
                    <button
                      onClick={saveShopDetails}
                      disabled={shopBusy}
                      data-testid="kit-shop-save-btn"
                      className="btn-volt px-4 py-2 rounded-md text-xs tracking-widest uppercase disabled:opacity-40"
                    >
                      {shopBusy ? "Saving…" : "Save shop details"}
                    </button>
                  </div>
                ) : (
                  <div className="text-sm space-y-1">
                    <div><span className="opacity-60 text-xs uppercase tracking-widest mr-2">Shop:</span>{openDetail.shop_name || <span className="opacity-40">—</span>}</div>
                    <div><span className="opacity-60 text-xs uppercase tracking-widest mr-2">Contact:</span>{openDetail.shop_contact || <span className="opacity-40">—</span>}</div>
                    <div><span className="opacity-60 text-xs uppercase tracking-widest mr-2">Order #:</span>{openDetail.shop_order_number || <span className="opacity-40">—</span>}</div>
                  </div>
                )}
              </div>

              <div className="mt-6">
                <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-2">Items ({openDetail.items?.length || 0}){openDetail.viewer_scope === "line" && <span className="opacity-60 ml-2 normal-case tracking-normal">· your items only</span>}</div>
                <div className="border border-border rounded overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-muted/40 text-[10px] tracking-widest uppercase opacity-60">
                      <th className="text-left px-3 py-2">Qty</th><th className="text-left px-3 py-2">Item</th><th className="text-left px-3 py-2">Colour</th><th className="text-left px-3 py-2">Size</th><th className="text-left px-3 py-2">#/Init</th><th className="text-left px-3 py-2">For</th>{isAdmin && <th className="text-left px-3 py-2">Coach</th>}<th className="text-left px-3 py-2">Delivered</th>
                    </tr></thead>
                    <tbody>
                      {(openDetail.items || []).map((l, i) => (
                        <tr key={`${l.item_name}-${l.color || ""}-${l.size || ""}-${l.assigned_to || ""}-${i}`} className="border-t border-border">
                          <td className="px-3 py-2 font-medium">{l.quantity}×</td>
                          <td className="px-3 py-2">{l.item_name}</td>
                          <td className="px-3 py-2 opacity-70">{l.color || "—"}</td>
                          <td className="px-3 py-2 opacity-70">{l.size || "—"}</td>
                          <td className="px-3 py-2 opacity-70 font-mono">{l.squad_ref || "—"}</td>
                          <td className="px-3 py-2 opacity-70">{l.assigned_to || "—"}</td>
                          {isAdmin && (
                            <td className="px-3 py-2 text-xs">
                              {l.coach_id && l.coach_id !== openDetail.coach_id ? (
                                <span className="px-2 py-0.5 rounded border border-[#83B68B]/40 text-[#83B68B] bg-[#83B68B]/10">{l.coach_name || l.coach_id}</span>
                              ) : (
                                <span className="opacity-40">— owner —</span>
                              )}
                            </td>
                          )}
                          <td className="px-3 py-2 text-xs">
                            {isAdmin && openDetail.status !== "cancelled" ? (
                              <div className="flex items-center gap-2 min-w-[180px]">
                                <input type="checkbox" checked={!!l.delivered_at} onChange={e => setLineDelivery(openDetail, i, e.target.checked)} className="accent-[#83B68B] w-4 h-4" data-testid={`kit-line-delivered-${i}`} />
                                <input defaultValue={l.delivery_note || ""} key={`${openDetail.id}-${i}-${l.delivery_note || ""}`} placeholder="Note…"
                                  onBlur={e => { if ((e.target.value || "") !== (l.delivery_note || "")) setLineDelivery(openDetail, i, !!l.delivered_at, e.target.value); }}
                                  onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                                  className="flex-1 bg-transparent border-b border-border focus:border-[#83B68B] outline-none text-xs py-0.5" data-testid={`kit-line-note-${i}`} />
                              </div>
                            ) : (
                              <div data-testid={`kit-line-delivery-${i}`}>
                                {l.delivered_at ? <span className="text-[#83B68B] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {format(parseISO(l.delivered_at), "d MMM")}</span> : <span className="opacity-40">—</span>}
                                {l.delivery_note && <div className="opacity-70 italic">"{l.delivery_note}"</div>}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-6">
                <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-2">History</div>
                <div className="space-y-1.5 text-xs">
                  {(openDetail.status_history || []).map((h, i) => (
                    <div key={`${h.status}-${h.at}-${i}`} className="flex items-baseline gap-2">
                      <span className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded border ${STATUS_TONE[h.status]}`}>{STATUS_LABEL[h.status]}</span>
                      <span className="opacity-60">{h.at && format(parseISO(h.at), "d MMM yyyy HH:mm")}</span>
                      <span className="opacity-70">· by {h.by_name}</span>
                      {h.note && <span className="opacity-80 italic">"{h.note}"</span>}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Catalogue dialog */}
      <CatalogueDialog open={openCatalogue} onOpenChange={setOpenCatalogue} catalogue={catalogue} onChange={load} />
    </div>
  );
}

function CatalogueDialog({ open, onOpenChange, catalogue, onChange }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ item_name: "", category: "kit", product_code: "", colors: "", sizes: "", active: true, entitlement: "none", paired_item_id: "none" });
  const [busy, setBusy] = useState(false);
  const openEdit = (c) => { setEditing(c); setForm({ item_name: c.item_name, category: c.category || "kit", product_code: c.product_code || "", colors: (c.colors || []).join(", "), sizes: (c.sizes || []).join(", "), active: c.active !== false, entitlements: itemEnts(c), paired_item_id: c.paired_item_id || "none" }); };
  const openNew = () => { setEditing({}); setForm({ item_name: "", category: "kit", product_code: "", colors: "", sizes: "", active: true, entitlements: [], paired_item_id: "none" }); };
  const save = async () => {
    if (!form.item_name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const body = {
        item_name: form.item_name.trim(),
        category: form.category,
        product_code: form.product_code.trim() || null,
        colors: form.colors.split(",").map(s => s.trim()).filter(Boolean),
        sizes: form.sizes.split(",").map(s => s.trim()).filter(Boolean),
        active: form.active,
        entitlements: form.entitlements,
        paired_item_id: form.paired_item_id === "none" ? null : form.paired_item_id,
      };
      if (editing?.id) await api.put(`/admin/kit/catalogue/${editing.id}`, body);
      else await api.post(`/admin/kit/catalogue`, body);
      toast.success("Saved");
      setEditing(null);
      onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Remove "${c.item_name}"?`)) return;
    try { await api.delete(`/admin/kit/catalogue/${c.id}`); toast.success("Removed"); onChange(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Product catalogue</DialogTitle></DialogHeader>
        <p className="text-xs opacity-60 mb-4">Kit, equipment and first-aid products (with supplier codes, colours and sizes) that coaches pick from. The bulk-upload template is generated from this list.</p>
        {editing ? (
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-widest opacity-70">Item name</Label><Input value={form.item_name} onChange={e=>setForm({...form, item_name: e.target.value})} data-testid="cat-form-name" className="mt-2" placeholder="Shirt" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-widest opacity-70">Category</Label>
                <Select value={form.category} onValueChange={v=>setForm({...form, category: v})}>
                  <SelectTrigger className="mt-2" data-testid="cat-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="kit">Kit</SelectItem><SelectItem value="equipment">Equipment</SelectItem><SelectItem value="first_aid">First Aid Supplies</SelectItem></SelectContent>
                </Select></div>
              <div><Label className="text-xs uppercase tracking-widest opacity-70">Product code <span className="opacity-50">(supplier SKU)</span></Label><Input value={form.product_code} onChange={e=>setForm({...form, product_code: e.target.value})} data-testid="cat-form-code" className="mt-2" placeholder="e.g. JK-SHIRT-01" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-widest opacity-70">Colours <span className="opacity-50">(comma-separated)</span></Label><Input value={form.colors} onChange={e=>setForm({...form, colors: e.target.value})} data-testid="cat-form-colors" className="mt-2" placeholder="Red, Black, White" /></div>
            <div><Label className="text-xs uppercase tracking-widest opacity-70">Sizes <span className="opacity-50">(comma-separated)</span></Label><Input value={form.sizes} onChange={e=>setForm({...form, sizes: e.target.value})} data-testid="cat-form-sizes" className="mt-2" placeholder="5-6, 7-8, 9-10, 11-12, 13-14, S, M, L, XL" /></div>
            {form.category === "kit" && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-widest opacity-70">Entitlement <span className="opacity-50">(tick all that apply)</span></Label>
                  <div className="mt-2 space-y-1.5" data-testid="cat-form-entitlements">
                    {ENT_OPTIONS.map(([k, label]) => (
                      <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" className="accent-[#83B68B]" checked={form.entitlements.includes(k)} data-testid={`cat-form-ent-${k}`}
                          onChange={e => setForm({ ...form, entitlements: e.target.checked ? [...form.entitlements, k] : form.entitlements.filter(x => x !== k) })} />
                        {label}
                      </label>
                    ))}
                    <div className="text-[10px] opacity-50">None ticked = general item anyone can order. Coaches get one Primary + one Secondary item per season: e.g. Polo <em>or</em> T-shirt (Primary), Padded <em>or</em> Rain jacket (Secondary).</div>
                  </div>
                </div>
                <div><Label className="text-xs uppercase tracking-widest opacity-70">Auto-pair with <span className="opacity-50">(same size & number)</span></Label>
                  <Select value={form.paired_item_id} onValueChange={v=>setForm({...form, paired_item_id: v})}>
                    <SelectTrigger className="mt-2" data-testid="cat-form-pair"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— none —</SelectItem>
                      {catalogue.filter(c => c.id !== editing?.id && (c.category || "kit") === "kit").map(c => <SelectItem key={c.id} value={c.id}>{c.item_name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={save} disabled={busy} data-testid="cat-form-save" className="btn-volt flex-1 py-2.5 rounded-md disabled:opacity-50">{busy ? "Saving…" : editing.id ? "Save" : "Add item"}</button>
              <button onClick={()=>setEditing(null)} className="px-4 py-2.5 rounded-md border border-border hover:border-[#83B68B] hover:text-[#83B68B]">Back</button>
            </div>
          </div>
        ) : (
          <>
            <button onClick={openNew} className="btn-volt px-4 py-2 rounded-md text-sm mb-4 inline-flex items-center gap-1.5" data-testid="cat-new-btn"><Plus className="w-4 h-4" /> Add item</button>
            {catalogue.length === 0 ? (
              <div className="opacity-60 text-sm">No items yet.</div>
            ) : (
              <div className="space-y-2">
                {catalogue.map(c => (
                  <div key={c.id} className="flex items-center gap-3 p-3 rounded border border-border" data-testid={`cat-row-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium"><span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border border-[#83B68B]/40 text-[#83B68B] mr-2">{({kit:"Kit",equipment:"Equipment",first_aid:"First Aid"})[c.category || "kit"]}</span>{c.product_code && <span className="font-mono text-xs opacity-60 mr-2">{c.product_code}</span>}{c.item_name}{c.active === false && <span className="ml-2 text-[10px] tracking-widest uppercase opacity-50">(inactive)</span>}</div>
                      <div className="text-[10px] opacity-60 mt-0.5">
                        {(c.colors || []).length > 0 ? `Colours: ${c.colors.join(", ")}` : "No colours"} · {(c.sizes || []).length > 0 ? `Sizes: ${c.sizes.join(", ")}` : "No sizes"}
                        {itemEnts(c).length > 0 && <span className="ml-2 text-[#83B68B]" data-testid={`cat-ents-${c.id}`}>· {itemEnts(c).map(e => ENT_LABEL[e]).join(", ")}</span>}
                        {c.paired_item_id && <span className="ml-2">· pairs with {catalogue.find(x => x.id === c.paired_item_id)?.item_name || "?"}</span>}
                      </div>
                    </div>
                    <button onClick={()=>openEdit(c)} className="p-1.5 opacity-60 hover:opacity-100 hover:text-[#83B68B]" title="Edit"><Pencil className="w-4 h-4" /></button>
                    <button onClick={()=>remove(c)} className="p-1.5 opacity-60 hover:opacity-100 hover:text-red-500" title="Remove"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
